/**
 * WS4 Analysis modal — tabbed analytics surface for the post-session
 * data review. Five tabs: Summary, Participation, Sentiment &
 * Readiness, Mode Preferences, Key Findings.
 *
 * Faithfully ported from `_legacy/ws4-assetlocker.js` lines 296-532
 * (`#analysisModal`). The content lives in
 * `./analysis-content.ts` as plain data; this file is layout-only.
 *
 * Visual contract:
 *   - Uses `PanelDialogContent` (Radix dialog, no built-in ✕)
 *   - 3-col title bar matching the panel-style modals in
 *     `src/components/shell/*` (XaesAgentModal, MeetCrewModal,
 *     PowerUserModal)
 *   - Outline + true-blue glow chrome described in the task brief
 *   - Footer: "XAES Phase Zero | WS4 — Human-Agent Ways of Working"
 *
 * Typography:
 *   - Uses the legacy `dt-*` class system from `src/index.css`
 *     (`.dt-kpi-grid`, `.dt-kpi-card`, `.dt-section-title`,
 *     `.dt-table`, `.dt-pill*`, `.dt-bar-bg`, `.dt-bar-fill`) so the
 *     React surface is byte-equivalent to legacy on font-size,
 *     spacing, and colour tokens. Inline literals (`10px`, `13px`)
 *     are preserved verbatim where legacy uses them inline.
 *
 * Theme:
 *   - All colours route through CSS variables — see
 *     `src/index.css` for the light/dark token sets
 *   - `--accent-danger` is defined in `src/index.css` and used via
 *     `tone("danger")` for the risk/issue chrome.
 */

import { useEffect, useState, type ReactNode, type CSSProperties } from "react";
import { BarChart3, X } from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import type { Workshop } from "@/content/workshops";
import { useFindings, type Finding } from "@/stores/useFindings";
import {
  getAnalysis,
  type AccentToken,
  type AnalysisContent,
  type ChatTelemetry,
  type CompletionBar,
  type ConcernRow,
  type FindingRow,
  type Kpi,
  type ModeAssignmentRow,
  type ModeDistribution,
  type ModePreferenceRow,
  type NextStepRow,
  type OverviewRow,
  type ParticipationRow,
  type ActivityRow,
  type SentimentRow,
  type StatusPill,
  type RolePill,
} from "./analysis-content";
import { useGeneratedAnalysis } from "./use-generated-analysis";

type AnalysisModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

const EMPTY_FINDINGS: readonly Finding[] = Object.freeze([]);

/** Finding severity → the analysis accent vocabulary (for the priority cell). */
function severityToAccent(severity: Finding["severity"]): AccentToken {
  switch (severity) {
    case "high":
      return "danger";
    case "medium":
      return "gold";
    case "low":
      return "sky";
    default:
      return "muted";
  }
}

type TabId = "summary" | "participation" | "sentiment" | "modes" | "findings" | "chat";

const TABS: { id: TabId; label: string }[] = [
  { id: "summary", label: "Summary" },
  { id: "participation", label: "Participation" },
  { id: "sentiment", label: "Sentiment & Readiness" },
  { id: "modes", label: "Mode Preferences" },
  { id: "findings", label: "Key Findings" },
];

/** ADO #689 — only shown when `MC_CHAT_ANALYTICS` produced a section. */
const CHAT_TAB: { id: TabId; label: string } = { id: "chat", label: "Chat Interactions" };

/* ----------------------- Accent → CSS var map -------------------- */

const EM_DASH = "—";

// Monospace stack used for numeric/percentage figures. Resolves to
// the `--font-mono` token defined in `src/index.css` — Share Tech
// Mono is self-hosted there with a platform-monospace fallback for
// the FOUT window. Kept as a local constant (rather than inlining
// `var(--font-mono)` at every call site) so the intent reads at a
// glance and to keep this file's diff small.
const MONO_STACK = "var(--font-mono)";

/**
 * Returns the CSS colour for an accent token. `danger` routes to the
 * `--accent-danger` token; `purple` routes to `--accent-purple` (a
 * real token since Phase 5b — previously aliased to melon to match
 * legacy `_legacy/xaes.css` line 1389, which itself only aliased
 * because no purple swatch existed yet).
 */
function tone(accent: AccentToken | undefined): string {
  switch (accent) {
    case "true-blue":
      return "var(--accent-true-blue)";
    case "sky":
      return "var(--accent-sky)";
    case "gold":
      return "var(--accent-gold)";
    case "green":
      return "var(--accent-green)";
    case "melon":
      return "var(--accent-melon)";
    case "purple":
      return "var(--accent-purple)";
    case "danger":
      return "var(--accent-danger)";
    case "muted":
      return "var(--text-muted)";
    default:
      return "var(--text-secondary)";
  }
}

/**
 * Maps an accent token to the legacy `.dt-pill-*` class. Per the
 * legacy mapping in `_legacy/xaes.css`: gold→orange, true-blue→blue,
 * green→green, sky→cyan, melon→melon, purple→purple. Purple was an
 * alias for melon in legacy (line 1389) but now resolves to the real
 * `--accent-purple` swatch — class names are preserved so the markup
 * still matches legacy verbatim. `danger` has no pill variant in
 * legacy — it always appears as cell text, never inside a pill — so
 * we route it through `.dt-pill-melon` defensively if it ever shows
 * up.
 */
function pillClass(accent: AccentToken): string {
  switch (accent) {
    case "gold":
      return "dt-pill-orange";
    case "true-blue":
      return "dt-pill-blue";
    case "green":
      return "dt-pill-green";
    case "sky":
      return "dt-pill-cyan";
    case "melon":
      return "dt-pill-melon";
    case "purple":
      return "dt-pill-purple";
    case "danger":
      return "dt-pill-melon";
    case "muted":
      return "dt-pill-blue";
  }
}

/* --------------------------- Atoms ------------------------------- */

function Pill({ pill }: { pill: StatusPill | RolePill }) {
  return <span className={`dt-pill ${pillClass(pill.accent)}`}>{pill.text}</span>;
}

function KpiTile({ kpi }: { kpi: Kpi }) {
  // Legacy KPI values default to `.dt-kpi-value` size. Word values
  // (e.g. "ON-THE-LOOP") step down to `--fs-base` + sans.
  const isWordValue = /^[A-Z]/.test(kpi.value) && kpi.value.length > 4;
  const valueStyle: CSSProperties = {
    color: tone(kpi.accent),
    ...(isWordValue
      ? { fontSize: "var(--fs-base)", fontFamily: "var(--font-sans)", fontWeight: 700 }
      : null),
  };
  return (
    <div className="dt-kpi-card">
      <div className="dt-kpi-label">{kpi.label}</div>
      <div className="dt-kpi-value" style={valueStyle}>
        {kpi.value}
        {kpi.valueSuffix ? (
          <span style={{ fontSize: "var(--fs-label)" }}>{kpi.valueSuffix}</span>
        ) : null}
      </div>
      <div className="dt-kpi-sub">{kpi.sub}</div>
    </div>
  );
}

function KpiRow({ kpis, columns }: { kpis: Kpi[]; columns?: number }) {
  // Legacy default is 4 columns (defined in `.dt-kpi-grid`). The
  // Summary and Sentiment panes override to 5 columns via inline
  // style — mirror that exactly.
  const style: CSSProperties | undefined =
    columns && columns !== 4
      ? { gridTemplateColumns: `repeat(${columns}, 1fr)` }
      : undefined;
  return (
    <div className="dt-kpi-grid" style={style}>
      {kpis.map((k) => (
        <KpiTile kpi={k} key={k.label} />
      ))}
    </div>
  );
}

function SectionTitle({
  children,
  marginTop,
}: {
  children: ReactNode;
  marginTop?: number;
}) {
  // Legacy: secondary section titles inside a pane always carry
  // `style="margin-top:16px;"`. The first section title in a pane
  // has no margin-top override and falls back to the class default
  // of `margin: 4px 0 8px;`.
  const style: CSSProperties | undefined =
    typeof marginTop === "number" ? { marginTop } : undefined;
  return (
    <div className="dt-section-title" style={style}>
      {children}
    </div>
  );
}

/* ------- Inline styles for ad-hoc cell highlights in tables ------ */

const TD_PRIMARY: CSSProperties = {
  fontWeight: 600,
  color: "var(--text-primary)",
};

/**
 * Inline style for activity-completion / mode-distribution progress
 * bar labels. Tokenized at `--fs-label` (a11y floor).
 */
const BAR_LABEL_STYLE: CSSProperties = {
  fontSize: "var(--fs-label)",
  color: "var(--text-secondary)",
};

function ProgressBar({ pct, accent }: { pct: number; accent: AccentToken }) {
  return (
    <div className="dt-bar-bg">
      <div
        className="dt-bar-fill"
        style={{
          width: `${Math.max(0, Math.min(100, pct))}%`,
          background: tone(accent),
        }}
      />
    </div>
  );
}

/* --------------------------- Summary ----------------------------- */

function SummaryPane({ data }: { data: AnalysisContent["summary"] }) {
  return (
    <>
      <KpiRow kpis={data.kpis} columns={5} />

      <SectionTitle>Workshop 4 — Session Overview</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Dimension</th>
            <th>Detail</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.overview.map((row: OverviewRow) => (
            <tr key={row.dimension}>
              <td style={TD_PRIMARY}>{row.dimension}</td>
              <td>{row.detail}</td>
              <td>
                <Pill pill={row.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionTitle marginTop={16}>Activity Completion Tracker</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.completion.map((c: CompletionBar) => (
          <div key={c.label}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 3,
              }}
            >
              <span style={BAR_LABEL_STYLE}>{c.label}</span>
              <span
                style={{
                  fontSize: "var(--fs-label)",
                  color: tone(c.accent),
                  fontFamily: MONO_STACK,
                }}
              >
                {c.pct}%
              </span>
            </div>
            <ProgressBar pct={c.pct} accent={c.accent} />
          </div>
        ))}
      </div>
    </>
  );
}

/* ------------------------ Participation -------------------------- */

function ParticipationPane({ data }: { data: AnalysisContent["participation"] }) {
  return (
    <>
      <KpiRow kpis={data.kpis} />

      <SectionTitle>Participation by Role Cluster</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Role Cluster</th>
            <th>Headcount</th>
            <th>Contribution Score</th>
            <th>Outputs Submitted</th>
            <th>Avg Engagement</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {data.rolesTable.map((row: ParticipationRow) => (
            <tr key={row.cluster.text}>
              <td>
                <Pill pill={row.cluster} />
              </td>
              <td>{row.headcount}</td>
              <td style={{ color: tone(row.contributionAccent) }}>
                {row.contributionScore}
              </td>
              <td>{row.outputs}</td>
              <td style={{ color: tone(row.engagementAccent) }}>
                {row.engagement}
              </td>
              <td>{row.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionTitle marginTop={16}>Contribution by Activity</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Activity</th>
            <th>Groups Formed</th>
            <th>Outputs Submitted</th>
            <th>Avg Quality (peer)</th>
            <th>Time Used</th>
          </tr>
        </thead>
        <tbody>
          {data.activitiesTable.map((row: ActivityRow) => (
            <tr key={row.activity}>
              <td>{row.activity}</td>
              <td>{row.groups}</td>
              <td>{row.outputs}</td>
              <td style={{ color: tone(row.qualityAccent) }}>{row.quality}</td>
              <td>{row.time}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* -------------------------- Sentiment ---------------------------- */

function SentimentPane({ data }: { data: AnalysisContent["sentiment"] }) {
  return (
    <>
      <KpiRow kpis={data.kpis} columns={5} />

      <SectionTitle>Sentiment Breakdown — Pre vs Post Session</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Statement</th>
            <th>Pre-Score</th>
            <th>Post-Score</th>
            <th>Delta</th>
            <th>Signal</th>
          </tr>
        </thead>
        <tbody>
          {data.sentimentTable.map((row: SentimentRow) => (
            <tr key={row.statement}>
              <td>{row.statement}</td>
              <td style={{ color: tone(row.preAccent) }}>{row.preScore}</td>
              <td style={{ color: tone(row.postAccent) }}>{row.postScore}</td>
              <td style={{ color: tone(row.deltaAccent), fontWeight: 700 }}>
                {row.delta}
              </td>
              <td>
                <Pill pill={row.signal} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionTitle marginTop={16}>Residual Concerns Logged</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Theme</th>
            <th>Frequency</th>
            <th>Severity</th>
            <th>Owner</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.concerns.map((row: ConcernRow) => (
            <tr key={row.theme}>
              <td>{row.theme}</td>
              <td>{row.frequency}</td>
              <td style={{ color: tone(row.severityAccent) }}>{row.severity}</td>
              <td>{row.owner}</td>
              <td>
                <Pill pill={row.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* ---------------------------- Modes ------------------------------ */

function ModesPane({ data }: { data: AnalysisContent["modes"] }) {
  return (
    <>
      <KpiRow kpis={data.kpis} />

      <SectionTitle>Operating Mode Preference Distribution</SectionTitle>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 16,
        }}
      >
        {data.distribution.map((d: ModeDistribution) => (
          <div key={d.mode}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: "var(--fs-label)",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                }}
              >
                {d.mode}{" "}
                <span style={{ fontWeight: 400, color: "var(--text-muted)" }}>
                  {d.description}
                </span>
              </span>
              <span
                style={{
                  fontSize: "var(--fs-label)",
                  color: tone(d.accent),
                  fontFamily: MONO_STACK,
                }}
              >
                {d.pct}%
              </span>
            </div>
            <ProgressBar pct={d.pct} accent={d.accent} />
          </div>
        ))}
      </div>

      <SectionTitle>Mode Preference by Role Cluster</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Role Cluster</th>
            <th>In-The-Loop</th>
            <th>Above-The-Loop</th>
            <th>On-The-Loop</th>
            <th>Out-Of-Loop</th>
            <th>Primary Reason</th>
          </tr>
        </thead>
        <tbody>
          {data.preferencesTable.map((row: ModePreferenceRow) => (
            <tr key={row.cluster.text}>
              <td>
                <Pill pill={row.cluster} />
              </td>
              <td
                style={{
                  color: row.inLoopAccent ? tone(row.inLoopAccent) : undefined,
                  fontWeight: row.inLoopAccent ? 600 : undefined,
                }}
              >
                {row.inLoop}
              </td>
              <td
                style={{
                  color: row.aboveLoopAccent
                    ? tone(row.aboveLoopAccent)
                    : undefined,
                  fontWeight: row.aboveLoopAccent ? 600 : undefined,
                }}
              >
                {row.aboveLoop}
              </td>
              <td
                style={{
                  color: row.onLoopAccent ? tone(row.onLoopAccent) : undefined,
                  fontWeight: row.onLoopAccent ? 600 : undefined,
                }}
              >
                {row.onLoop}
              </td>
              <td
                style={{
                  color: row.outLoopAccent
                    ? tone(row.outLoopAccent)
                    : undefined,
                  fontWeight: row.outLoopAccent ? 600 : undefined,
                }}
              >
                {row.outLoop}
              </td>
              <td>{row.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionTitle marginTop={16}>Agreed Mode Assignments — Draft Matrix</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Process / Task Domain</th>
            <th>Agreed Mode</th>
            <th>Rationale</th>
            <th>Review Date</th>
          </tr>
        </thead>
        <tbody>
          {data.assignmentsTable.map((row: ModeAssignmentRow) => (
            <tr key={row.domain}>
              <td>{row.domain}</td>
              <td>
                <Pill pill={row.mode} />
              </td>
              <td>{row.rationale}</td>
              <td>{row.reviewDate}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* --------------------------- Findings ---------------------------- */

function FindingsPane({
  data,
  liveFindings,
}: {
  data: AnalysisContent["findings"];
  liveFindings: readonly Finding[];
}) {
  // CONV — live findings are authoritative (same SSoT as the Log/Repository);
  // the curated WS4 sample is the fallback when none have been logged.
  const rows: FindingRow[] =
    liveFindings.length > 0
      ? liveFindings.map((f, i) => ({
          id: `F-${String(i + 1).padStart(2, "0")}`,
          finding: f.detail ? `${f.title} — ${f.detail}` : f.title,
          category: { text: f.domain.toUpperCase(), accent: "true-blue" },
          priority: f.severity,
          priorityAccent: severityToAccent(f.severity),
          action: f.status,
        }))
      : data.findingsTable;
  return (
    <>
      <SectionTitle>Top Findings — WS4 Human-Agent Ways of Working</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Finding</th>
            <th>Category</th>
            <th>Priority</th>
            <th>Recommended Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: FindingRow) => (
            <tr key={row.id}>
              <td
                style={{
                  color: "var(--accent-sky)",
                  fontFamily: MONO_STACK,
                  fontWeight: 700,
                }}
              >
                {row.id}
              </td>
              <td>{row.finding}</td>
              <td>
                <Pill pill={row.category} />
              </td>
              <td
                style={{
                  color: tone(row.priorityAccent),
                  fontWeight: 600,
                }}
              >
                {row.priority}
              </td>
              <td>{row.action}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionTitle marginTop={16}>Recommended Next Steps</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Action</th>
            <th>Owner</th>
            <th>Due</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.nextSteps.map((row: NextStepRow) => (
            <tr key={row.action}>
              <td>{row.action}</td>
              <td>{row.owner}</td>
              <td>{row.due}</td>
              <td>
                <Pill pill={row.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* ---------------------- Chat Interactions ------------------------- */

/** ADO #689 — volume, topics, referenced outputs, and a per-participant
 *  summary derived from AI-chat turns on this workshop. Never renders raw
 *  chat text — only the already-sanitised fields the BFF persisted (see
 *  `chat_telemetry.py`'s privacy-by-construction design). */
function ChatTelemetryPane({ data }: { data: ChatTelemetry }) {
  return (
    <>
      <SectionTitle>Chat Volume</SectionTitle>
      <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
        <div>
          <div style={BAR_LABEL_STYLE}>Turns</div>
          <div style={{ fontSize: "var(--fs-h2)", fontFamily: MONO_STACK }}>
            {data.volumeMetrics.turnCount}
          </div>
        </div>
        <div>
          <div style={BAR_LABEL_STYLE}>Participants</div>
          <div style={{ fontSize: "var(--fs-h2)", fontFamily: MONO_STACK }}>
            {data.volumeMetrics.participantCount}
          </div>
        </div>
      </div>

      <SectionTitle>Topics Discussed</SectionTitle>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
        {data.topics.length === 0 ? (
          <span style={{ color: "var(--text-muted)" }}>No topics captured yet.</span>
        ) : (
          data.topics.map((topic) => (
            <span key={topic} className="dt-pill dt-pill-blue">
              {topic}
            </span>
          ))
        )}
      </div>

      <SectionTitle>Referenced Outputs</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Output</th>
            <th>Times Referenced</th>
          </tr>
        </thead>
        <tbody>
          {data.referencedOutputs.map((row) => (
            <tr key={row.outputId}>
              <td>{row.label}</td>
              <td>{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <SectionTitle marginTop={16}>Participant Breakdown</SectionTitle>
      <table className="dt-table">
        <thead>
          <tr>
            <th>Participant</th>
            <th>Turns</th>
            <th>Summary</th>
          </tr>
        </thead>
        <tbody>
          {data.participantBreakdown.map((row) => (
            <tr key={row.identity}>
              <td>{row.identity}</td>
              <td>{row.turnCount}</td>
              <td>{row.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* --------------------------- Empty ------------------------------- */

function EmptyPane({
  label,
  message,
}: {
  label: string;
  /** Overrides the default "not registered yet" copy — used to distinguish
   *  loading/error states from a genuine "nothing generated yet" empty
   *  state (ADO #689, mirrors `DataTablesModal`'s `EmptyShell`). */
  message?: string;
}) {
  return (
    <div
      style={{
        padding: "32px 12px",
        textAlign: "center",
        color: "var(--text-muted)",
        fontSize: "var(--fs-sub)",
        letterSpacing: 0.5,
      }}
    >
      <div style={{ fontSize: "1.75rem", marginBottom: 8 }}>{EM_DASH}</div>
      {message ?? `No ${label} data registered for this workshop yet.`}
    </div>
  );
}

/* --------------------------- Modal ------------------------------- */

export function AnalysisModal({ open, onOpenChange, workshopId }: AnalysisModalProps) {
  const [tab, setTab] = useState<TabId>("summary");
  // ADO #689 — a real, generated Analysis (from this workshop's actual
  // transcript/decisions/findings/actions) takes priority when the BFF has
  // produced one; the static WS4 fixture is now purely a fallback for WS4
  // when nothing has been generated yet, never a stand-in for a real
  // workshop's data.
  const generated = useGeneratedAnalysis(workshopId);
  const data = generated.data ?? getAnalysis(workshopId);
  const tabs = data?.chatTelemetry ? [...TABS, CHAT_TAB] : TABS;
  const wsId = workshopId as Workshop["id"];

  // CONV — live findings drive the Key Findings tab (same SSoT as the
  // Log/Repository); hydrated lazily on open (idempotent).
  const liveFindings = useFindings(
    (s) => s.entriesByWorkshop[wsId] ?? (EMPTY_FINDINGS as Finding[]),
  );
  useEffect(() => {
    if (open) void useFindings.getState().hydrate();
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[1080px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">Analysis — Workshop {workshopId}</DialogTitle>
        <DialogDescription className="sr-only">
          Post-session analytics for {workshopId}, including participation, sentiment,
          mode preferences, and key findings.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "85vh" }}
        >
          {/* HEADER — legacy `.modal-header` is `space-between` flex:
              title block on the LEFT, action cluster on the right.
              Mirrors `_legacy/ws4-assetlocker.js` lines 299-311. */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <BarChart3
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
              />
              <span className="modal-title">ANALYSIS</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-gold-rgb), 0.12)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.35)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-gold)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                ASSET LOCKER
              </span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-sky-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-sky-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-sky)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                {workshopId} — HUMAN-AGENT WAYS OF WORKING
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: "var(--accent-green)",
                  boxShadow: "var(--glow-green)",
                  animation: "pulse 2s infinite",
                }}
              />
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                Post-Session
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* TAB BAR — legacy `.dt-tab-bar` */}
          <div className="dt-tab-bar" role="tablist" aria-label="Analysis sections">
            {tabs.map((t) => {
              const isActive = t.id === tab;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setTab(t.id)}
                  className={isActive ? "dt-tab dt-tab-active" : "dt-tab"}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* BODY — legacy `.modal-body` (overflow + scrollbar styling
              come from the class). The inner `.dt-pane.dt-pane-active`
              provides the 16 px gutter. */}
          <div className="modal-body">
            <div className="dt-pane dt-pane-active">
              {generated.loading ? (
                <EmptyPane label="analysis" message="Loading generated analysis…" />
              ) : data === null ? (
                <EmptyPane
                  label="analysis"
                  message={
                    generated.error
                      ? "Couldn't load the generated analysis — try again shortly."
                      : undefined
                  }
                />
              ) : tab === "summary" ? (
                <SummaryPane data={data.summary} />
              ) : tab === "participation" ? (
                <ParticipationPane data={data.participation} />
              ) : tab === "sentiment" ? (
                <SentimentPane data={data.sentiment} />
              ) : tab === "modes" ? (
                <ModesPane data={data.modes} />
              ) : tab === "findings" ? (
                <FindingsPane data={data.findings} liveFindings={liveFindings} />
              ) : data.chatTelemetry ? (
                <ChatTelemetryPane data={data.chatTelemetry} />
              ) : (
                <EmptyPane label="chat interaction" />
              )}
            </div>
          </div>

          {/* FOOTER — legacy `.modal-footer` chrome */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">Asset Locker — Analysis</span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: "var(--accent-sky)" }}
            >
              {workshopId} — Human-Agent Ways of Working
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
